import type { ComponentOrReferenceFormattingRule } from '@ardoq/data-model';
import { Multiselect } from '@ardoq/select';
import type { FormattingContextState } from '../types';
import { useFormattingContext } from '../useFormattingContext';
import { APIEntityType } from '@ardoq/api-types';
import {
  getAllTypesLabel,
  getTypePickerUpdate,
  normalizeTypeNames,
} from '../utils';
import { StatusType } from '@ardoq/status-ui';

type FormattingRuleTypePickerProperties = {
  rule: ComponentOrReferenceFormattingRule;
  formattingContext: FormattingContextState;
  menuPortalTarget?: HTMLElement | null;
  ariaLabel?: string;
};
const FormattingRuleTypePicker = ({
  rule,
  formattingContext,
  menuPortalTarget,
  ariaLabel,
}: FormattingRuleTypePickerProperties) => {
  const { __id: ruleId, type, typeNames, field } = rule;
  const { i18nLocale } = useFormattingContext();
  const { updateRule, componentTypeNames, referenceTypeNames } =
    formattingContext;
  const availableTypeNames =
    type === APIEntityType.COMPONENT ? componentTypeNames : referenceTypeNames;
  const allTypesLabel = getAllTypesLabel(i18nLocale, type);

  const savedButMissing = (typeNames ?? []).filter(
    name => !availableTypeNames.includes(name)
  );

  const typeOptions = [
    ...savedButMissing.map(name => ({
      value: name,
      label: name,
      statusType: StatusType.ERROR,
    })),
    ...availableTypeNames.map(name => ({ value: name, label: name })),
  ];

  const handleTypeNamesChange = (selectedTypeNames: string[] | null) => {
    const newTypeNames = normalizeTypeNames(selectedTypeNames);
    const update = getTypePickerUpdate(newTypeNames, field);
    updateRule(ruleId, update);
  };

  return (
    <Multiselect
      aria-label={ariaLabel}
      value={typeNames ?? []}
      placeholder={allTypesLabel}
      options={typeOptions}
      showSelectAllFooter
      shouldTruncateSelectedOptions
      onValueChange={handleTypeNamesChange}
      menuPortalTarget={menuPortalTarget}
    />
  );
};
export { FormattingRuleTypePicker };
